import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { mockProducts } from '../../mocks/products';
import { useCheckedProducts } from '../../hooks/useCheckedProducts';
import CompareBar from './components/CompareBar';

export default function ProductsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [compareIds, setCompareIds] = useState<string[]>([]);

  const { checkedProducts, toggleCheck } = useCheckedProducts();

  // Convert database products to display format
  const discoveredAsProducts = useMemo(() => {
    return checkedProducts.map(dp => ({
      id: dp.id,
      name: dp.name,
      category: dp.category,
      image: dp.image || '',
      commission: '—',
      clicks: 0,
      conversions: 0,
      earnings: 0,
      affiliateLink: dp.website || '#',
      description: dp.description || dp.tagline || '',
      rating: dp.rating || 0,
      reviews: dp.users_count || 0,
      pricing: dp.pricing_amount || 'Free',
      tags: dp.tags || [],
      features: dp.features || [],
      longDescription: dp.description || '',
      isFromDiscover: true,
      logo: dp.logo || '',
    }));
  }, [checkedProducts]);

  const allProducts = useMemo(() => {
    return [...mockProducts.map(p => ({ ...p, isFromDiscover: false, logo: '' })), ...discoveredAsProducts];
  }, [discoveredAsProducts]);

  const categories = ['All', ...Array.from(new Set(allProducts.map(p => p.category)))];

  const toggleCompare = (id: string) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((i) => i !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = allProducts;

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    const sorted = [...filtered];
    switch (sortBy) {
      case 'price-low':
        sorted.sort((a, b) => {
          const priceA = parseFloat(a.pricing.match(/\d+\.?\d*/)?.[0] || '0');
          const priceB = parseFloat(b.pricing.match(/\d+\.?\d*/)?.[0] || '0');
          return priceA - priceB;
        });
        break;
      case 'price-high':
        sorted.sort((a, b) => {
          const priceA = parseFloat(a.pricing.match(/\d+\.?\d*/)?.[0] || '0');
          const priceB = parseFloat(b.pricing.match(/\d+\.?\d*/)?.[0] || '0');
          return priceB - priceA;
        });
        break;
      case 'rating':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        sorted.sort((a, b) => b.reviews - a.reviews);
        break;
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return sorted;
  }, [searchQuery, selectedCategory, sortBy, allProducts]);

  return (
    <div className="min-h-screen bg-carbon-950">
      <Navbar />

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>
        <div className="absolute top-10 right-20 w-[400px] h-[400px] bg-neon/5 blur-[120px]"></div>
        <div className="absolute bottom-10 left-10 w-[300px] h-[300px] bg-neon/3 blur-[100px]"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 mb-8">
            <span className="w-1.5 h-1.5 bg-neon animate-pulse"></span>
            <span className="text-white/70 text-xs font-medium tracking-wide uppercase">
              Hand-picked &amp; reviewed by our team
            </span>
          </div>

          <h1 className="text-5xl lg:text-7xl font-bold text-white leading-[0.95] mb-6 tracking-tight">
            All <span className="text-neon">Products</span>
          </h1>
          <p className="text-base text-white/40 max-w-2xl mx-auto mb-12 leading-relaxed">
            Discover hand-picked tools and services with honest reviews, real comparisons, and exclusive
            deals.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center bg-white/10 border border-white/20 overflow-hidden hover:border-white/30 transition-colors">
              <div className="w-5 h-5 flex items-center justify-center ml-5">
                <i className="ri-search-line text-white/50 text-lg"></i>
              </div>
              <input
                type="text"
                placeholder="Search for tools, software, or categories..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="flex-1 px-4 py-4 text-sm text-white placeholder-white/50 focus:outline-none bg-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-4 text-white/30 hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Discovered Products Banner */}
      {discoveredAsProducts.length > 0 && (
        <section className="pb-4">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-3 px-4 py-3 bg-neon/5 border border-neon/15">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-compass-discover-line text-neon text-base"></i>
              </div>
              <span className="text-white/60 text-xs">
                <strong className="text-neon font-bold">{discoveredAsProducts.length} product{discoveredAsProducts.length !== 1 ? 's' : ''}</strong> added from Discover AGI
              </span>
              <Link to="/discover" className="ml-auto text-xs text-neon/70 hover:text-neon transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
                Go to Discover <i className="ri-arrow-right-s-line text-sm"></i>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Filters & Content */}
      <section className={`${compareIds.length > 0 ? 'pb-40' : 'pb-24'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Category Tabs + Sort */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-10">
            <div className="flex items-center gap-1 flex-wrap">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    selectedCategory === category
                      ? 'bg-neon text-black border-neon font-bold'
                      : 'bg-transparent text-white/50 border-white/10 hover:border-white/20 hover:text-white/80'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-white/30 text-xs uppercase tracking-wider">Sort:</span>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 text-white text-xs focus:outline-none focus:border-neon/50 cursor-pointer appearance-none pr-8"
                style={{
                  backgroundImage:
                    'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                }}
              >
                <option value="featured" className="bg-carbon-900">Featured</option>
                <option value="price-low" className="bg-carbon-900">Price: Low to High</option>
                <option value="price-high" className="bg-carbon-900">Price: High to Low</option>
                <option value="rating" className="bg-carbon-900">Highest Rated</option>
                <option value="reviews" className="bg-carbon-900">Most Reviews</option>
                <option value="name" className="bg-carbon-900">Name: A to Z</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-white/30 text-xs uppercase tracking-wider">
              Showing {filteredAndSortedProducts.length}{' '}
              {filteredAndSortedProducts.length === 1 ? 'product' : 'products'}
            </p>
            {compareIds.length === 0 && (
              <p className="text-white/20 text-xs flex items-center gap-1.5">
                <i className="ri-scales-3-line text-sm"></i>
                Click the compare icon to compare products
              </p>
            )}
          </div>

          {/* Products Grid */}
          {filteredAndSortedProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-product-shop>
              {filteredAndSortedProducts.map(product => {
                const isSelected = compareIds.includes(product.id);
                return (
                  <div
                    key={product.id}
                    className={`group bg-carbon-900 border overflow-hidden transition-all duration-300 relative ${
                      isSelected ? 'border-neon/40 shadow-[0_0_20px_rgba(0,255,136,0.06)]' : 'border-white/5 hover:border-neon/20'
                    }`}
                  >
                    {/* Discover badge */}
                    {product.isFromDiscover && (
                      <div className="absolute top-3 left-3 z-10 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                        <i className="ri-compass-discover-line text-[10px]"></i> From Discover
                      </div>
                    )}

                    {/* Compare Toggle */}
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
                      <button
                        onClick={() => toggleCompare(product.id)}
                        className={`w-8 h-8 flex items-center justify-center border transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-neon border-neon text-black'
                            : 'bg-carbon-900/80 backdrop-blur-sm border-white/20 text-white/50 hover:border-neon/50 hover:text-neon'
                        }`}
                        title={isSelected ? 'Remove from comparison' : 'Add to comparison'}
                      >
                        <i className={`${isSelected ? 'ri-check-line' : 'ri-scales-3-line'} text-sm`}></i>
                      </button>
                      {product.isFromDiscover && (
                        <button
                          onClick={() => toggleCheck(product.id)}
                          className="w-8 h-8 flex items-center justify-center border bg-carbon-900/80 backdrop-blur-sm border-white/20 text-red-400 hover:border-red-400/50 hover:text-red-400 transition-all cursor-pointer"
                          title="Remove from Products"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      )}
                    </div>

                    <Link
                      to={product.isFromDiscover ? product.affiliateLink : `/products/${product.id}`}
                      target={product.isFromDiscover ? '_blank' : undefined}
                      rel={product.isFromDiscover ? 'noopener noreferrer nofollow' : undefined}
                      className="block cursor-pointer"
                    >
                      <div className="relative w-full h-48 bg-carbon-950 overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.name}
                          title={`${product.name} - ${product.category}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 opacity-80"
                        />
                        <div className={`absolute ${product.isFromDiscover ? 'top-10' : 'top-3'} left-3 px-2.5 py-1 bg-white/10 backdrop-blur-sm text-white/80 text-xs font-medium whitespace-nowrap border border-white/10`}>
                          {product.category}
                        </div>
                      </div>

                      <div className="p-5">
                        <div className="flex items-start gap-3 mb-2">
                          {product.isFromDiscover && product.logo && (
                            <div className="w-8 h-8 flex-shrink-0 overflow-hidden border border-white/10">
                              <img src={product.logo} alt={product.name} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <h3 className="text-sm font-bold text-white group-hover:text-neon transition-colors">
                            {product.name}
                          </h3>
                        </div>
                        <p className="text-white/40 text-xs mb-4 line-clamp-2 leading-relaxed">{product.description}</p>

                        <div className="flex items-center gap-2 mb-4">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(star => (
                              <i
                                key={star}
                                className={`ri-star-${
                                  star <= Math.floor(product.rating)
                                    ? 'fill'
                                    : star - 0.5 <= product.rating
                                    ? 'half-fill'
                                    : 'line'
                                } text-neon text-xs`}
                              ></i>
                            ))}
                          </div>
                          <span className="text-xs text-white/30">{product.rating}</span>
                          <span className="text-xs text-white/30">
                            ({product.reviews.toLocaleString()})
                          </span>
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-white/5">
                          <span className="text-sm font-bold text-white">{product.pricing}</span>
                          <span className="text-xs text-neon font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                            {product.isFromDiscover ? 'Visit Site' : 'View Details'} <i className={`${product.isFromDiscover ? 'ri-external-link-line' : 'ri-arrow-right-s-line'}`}></i>
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-white/10">
                <i className="ri-search-line text-3xl text-white/20"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No products found</h3>
              <p className="text-white/40 text-sm">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </section>

      <Footer />

      <CompareBar
        selectedIds={compareIds}
        onRemove={(id) => setCompareIds((prev) => prev.filter((i) => i !== id))}
        onClear={() => setCompareIds([])}
      />
    </div>
  );
}
